/**
 * Видео в уроках: RuTube, ВК и YouTube.
 * apps/web/src/lib/video.tsx
 *
 * Зрители у нас из России, где YouTube не грузится: ролик оттуда для них — пустое место.
 * Поэтому у урока может быть несколько ссылок на один ролик, а показываем ту, что
 * откроется: сначала RuTube, потом ВК, и только в крайнем случае YouTube.
 */

// ── разбор ссылок ───────────────────────────────────────────────────────────

export function getYouTubeId(url: string): string | null {
  const m =
    url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/) ||
    url.match(/youtube\.com\/shorts\/([\w-]{11})/);
  return m ? m[1] : null;
}

export function isYouTubeShorts(url: string): boolean {
  return /youtube\.com\/shorts\//.test(url);
}

export function getRuTubeId(url: string): string | null {
  const m =
    url.match(/rutube\.ru\/video\/([a-zA-Z0-9]+)/) ||
    url.match(/rutube\.ru\/(?:shorts|yappy)\/([a-zA-Z0-9]+)/);
  return m ? m[1] : null;
}

export function isRuTubeShorts(url: string): boolean {
  return /rutube\.ru\/(?:shorts|yappy)\//.test(url);
}

/** ВКонтакте: vk.com/video-123_456, vkvideo.ru/video-123_456, готовая ссылка video_ext */
export function getVkVideo(url: string): { oid: string; id: string; hash?: string } | null {
  // Клипы ВК: vk.com/clip-117193376_456239039 — тот же встраиваемый плеер (07.09.2026)
  const clip = url.match(/(?:vk\.com|vkvideo\.ru|m\.vk\.com|vk\.ru)\/clip(-?\d+)_(\d+)/);
  if (clip) return { oid: clip[1], id: clip[2] };
  const ext = url.match(/video_ext\.php\?oid=(-?\d+)&id=(\d+)(?:&hash=([a-zA-Z0-9]+))?/);
  if (ext) return { oid: ext[1], id: ext[2], hash: ext[3] };

  const plain = url.match(/(?:vk\.com|vkvideo\.ru|m\.vk\.com)\/video(-?\d+)_(\d+)(?:%2F|\/)?([a-zA-Z0-9]+)?/);
  if (plain) return { oid: plain[1], id: plain[2], hash: plain[3] };

  return null;
}

export function isVideoUrl(url: string): boolean {
  // прямой файл (наши ролики на сервере) — тоже видео: в тексте урока становится проигрывателем
  return !!(getYouTubeId(url) || getRuTubeId(url) || getVkVideo(url) || isDirectVideoFile(url));
}

/** Кадр-постер для нашего файла: тот же адрес с .jpg */
export function posterFor(url: string): string {
  return url.replace(/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i, '.jpg');
}

/** Прямой файл (mp4 и подобное) — его показывает обычный проигрыватель браузера */
export function isDirectVideoFile(url: string): boolean {
  return /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url);
}

// ── выбор источника под место ───────────────────────────────────────────────

/**
 * Из нескольких ссылок на один ролик выбираем ту, что зритель точно увидит.
 *
 * Порядок один и тот же везде: RuTube, потом ВК, и только потом YouTube. Зрители у нас
 * из России, а YouTube там не грузится — ролик, вставленный оттуда, для них просто
 * пустое место. YouTube оставляем последним запасным вариантом, если другого нет.
 */
export function pickVideoUrl(urls: Array<string | undefined | null>): string | null {
  const list = urls.filter(Boolean).map(String);
  if (!list.length) return null;

  const ru = list.find((u) => getRuTubeId(u));
  const vk = list.find((u) => getVkVideo(u));
  const yt = list.find((u) => getYouTubeId(u));

  return [ru, vk, yt].find(Boolean) || list[0];
}

// ── сам проигрыватель ───────────────────────────────────────────────────────

/** Мы внутри мини-приложения Telegram */
function insideTelegram(): boolean {
  return typeof window !== 'undefined' && !!(window as any).Telegram?.WebApp?.initData;
}

/**
 * Внутри Telegram встроенные проигрыватели RuTube и ВК не запускаются, а YouTube
 * не открывается из России. Поэтому там — кнопка, которая отдаёт ролик наружу:
 * в браузер или приложение, где он играет.
 */
function OpenOutside({ url, service }: { url: string; service: string }) {
  const open = () => {
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(url);
    else window.open(url, '_blank', 'noopener');
  };
  return (
    <div className="my-4 rounded-xl border border-gray-700 bg-gray-800 p-4 text-center">
      <div className="text-3xl">🎬</div>
      <p className="mt-2 text-sm text-gray-300">Видео к уроку — на {service}</p>
      <button
        onClick={open}
        className="mt-3 w-full bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl py-3"
      >
        Смотреть видео
      </button>
      <p className="mt-2 text-xs text-gray-500">Откроется вне Telegram — здесь оно не проигрывается</p>
    </div>
  );
}

export function VideoEmbed({ url }: { url: string }) {
  const youtubeId = getYouTubeId(url);
  const rutubeId = getRuTubeId(url);
  const vk = getVkVideo(url);
  const isShorts = isYouTubeShorts(url) || isRuTubeShorts(url);
  const aspectRatio = isShorts ? '177.78%' : '56.25%'; // 9:16 или 16:9

  const frame = (src: string, title: string) => (
    <div className={`my-4 mx-auto ${isShorts ? 'max-w-[360px]' : 'w-full'}`}>
      <div className="relative w-full" style={{ paddingBottom: aspectRatio }}>
        <iframe
          className="absolute top-0 left-0 w-full h-full rounded-xl"
          src={src}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {/* Если встроенный проигрыватель не откроется — остаётся прямая ссылка */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block mt-1 text-xs text-gray-400 hover:text-gray-300 text-center"
      >
        Не открывается? Смотреть по ссылке
      </a>
    </div>
  );

  // В Telegram встроенные проигрыватели не работают — отдаём ролик наружу
  if (insideTelegram()) {
    if (rutubeId) return <OpenOutside url={url} service="RuTube" />;
    if (vk) return <OpenOutside url={url} service="ВКонтакте" />;
    if (youtubeId) return <OpenOutside url={url} service="YouTube" />;
  }

  if (youtubeId) return frame(`https://www.youtube.com/embed/${youtubeId}`, 'YouTube');
  if (rutubeId) return frame(`https://rutube.ru/play/embed/${rutubeId}`, 'RuTube');
  if (vk) {
    const hash = vk.hash ? `&hash=${vk.hash}` : '';
    return frame(`https://vk.com/video_ext.php?oid=${vk.oid}&id=${vk.id}${hash}&hd=2`, 'VK Видео');
  }

  if (isDirectVideoFile(url)) {
    return (
      <div className="my-4">
        {/* Постер — кадр с тем же именем и .jpg рядом с файлом (стандарт §9.1); без него плеер чёрный */}
        <video controls preload="metadata" playsInline className="block max-w-full max-h-[70vh] mx-auto rounded-xl bg-black" src={url} poster={posterFor(url)}>
          Ваш браузер не поддерживает видео
        </video>
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
      {url}
    </a>
  );
}
